/**
 * Console + batched POST /v1/client-errors ({ errors: ClientError[] }, at most
 * MAX_PER_POST per request). Sends when `batchSize` reports are queued or
 * `flushMs` after the first one. A failed send is dropped (logged to the
 * console): the error pipeline must not feed its own failures back into itself.
 */
import { ROUTES, type ApiClient } from '../api.js';
import { toReport, type CrashKind, type CrashReport, type CrashReporter } from './types.js';

/** One item of the API's ClientErrors body: message, stack and a free-form context. */
export interface ClientError {
  message: string;
  stack?: string;
  context: Record<string, string | number | boolean>;
}

/** The API accepts 1..20 errors per request. */
export const MAX_PER_POST = 20;

/** The API stores message, stack and context; everything else about the report travels in context. */
export function toClientError(r: CrashReport): ClientError {
  const { message, stack, context, ...meta } = r;
  const extra = Object.fromEntries(Object.entries(meta).filter(([, v]) => v !== undefined)) as ClientError['context'];
  return { message, ...(stack !== undefined ? { stack } : {}), context: { ...context, ...extra } };
}

export interface ApiCrashReporterOptions {
  api: ApiClient;
  release?: string;
  now?: () => number;
  url?: () => string;
  batchSize?: number;
  flushMs?: number;
  /** queue bound while offline */
  maxQueue?: number;
  console?: Pick<Console, 'error' | 'warn'>;
}

export class ApiCrashReporter implements CrashReporter {
  private queue: CrashReport[] = [];
  private timer: ReturnType<typeof setTimeout> | undefined;

  constructor(private readonly o: ApiCrashReporterOptions) {}

  private get log(): Pick<Console, 'error' | 'warn'> {
    return this.o.console ?? console;
  }

  capture(error: unknown, kind: CrashKind = 'manual', context?: CrashReport['context']): void {
    this.log.error('[crash]', error);
    const url = this.o.url?.() ?? (typeof location === 'undefined' ? undefined : location.href);
    this.queue.push(toReport(error, kind, (this.o.now ?? Date.now)(), { url, release: this.o.release, context }));
    if (this.queue.length > (this.o.maxQueue ?? 50)) this.queue.shift();
    if (this.queue.length >= (this.o.batchSize ?? 10)) void this.flushSafely();
    else this.timer ??= setTimeout(() => void this.flushSafely(), this.o.flushMs ?? 5000);
  }

  async flush(): Promise<void> {
    clearTimeout(this.timer);
    this.timer = undefined;
    if (!this.queue.length) return;
    const reports = this.queue;
    this.queue = [];
    for (let i = 0; i < reports.length; i += MAX_PER_POST) {
      await this.o.api.call('POST', ROUTES.clientErrors, { errors: reports.slice(i, i + MAX_PER_POST).map(toClientError) });
    }
  }

  private flushSafely(): Promise<void> {
    return this.flush().catch((e: unknown) => this.log.warn('[crash] could not send client errors', e));
  }
}
